import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './YouthSummit.css';
import summitHero from '../assets/YOCILAB Webiste photos/YOCILAB YOuth Summit/REMS0044.jpg';
import summitBanner from '../assets/YOCILAB Webiste photos/YOCILAB Youth Summit 2026 (2).png';
import campusTourImg from '../assets/YOCILAB Webiste photos/YOCILAB YOuth Summit/DSC_4604.jpg';
import experienceImg from '../assets/YOCILAB Webiste photos/YOCILAB YOuth Summit/DSC_4561.jpg';
import challengeImg from '../assets/YOCILAB Webiste photos/YOCILAB YOuth Summit/6.png';
import fellowshipImg from '../assets/YOCILAB Webiste photos/YOCILAB YOuth Summit/SheLead Circle.jpg';
import introImg from '../assets/YOCILAB Webiste photos/YOCILAB YOuth Summit/yOCILAB Youth (1).png';

// Countdown to Oct 31 2026
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calculate = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) return setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  return timeLeft;
}

const pillars = [
  {
    title: 'Campus Tour',
    subtitle: 'Pre-Summit Outreach',
    desc: 'Bringing the Summit to schools, universities, and youth communities through career talks, leadership sessions, and opportunity awareness.',
    path: '/youth-summit/campus-tour',
    img: campusTourImg,
    cta: 'Explore the Campus Tour',
  },
  {
    title: 'Summit Experience',
    subtitle: '30th – 31st Oct 2026',
    desc: 'The flagship two-day event where delegates learn from industry leaders, connect with employers and partners, and discover new opportunities.',
    path: '/youth-summit/summit-experience',
    img: experienceImg,
    cta: 'Experience the Summit',
    highlight: true,
  },
  {
    title: 'Innovation Challenge',
    subtitle: 'Overnight Hack Night',
    desc: 'Teams collaborate to solve real-world challenges, receive expert mentorship, and pitch innovative solutions to a panel of judges.',
    path: '/youth-summit/innovation-challenge',
    img: challengeImg,
    cta: 'Register Your Team',
  },
  {
    title: 'YOCILAB Fellowship',
    subtitle: '6-Month Program',
    desc: 'A post-Summit program supporting 20 outstanding participants through mentorship, leadership development, and continued access to opportunities.',
    path: '/youth-summit/fellowship',
    img: fellowshipImg,
    cta: 'Learn About Fellowship',
  },
];

const audienceCards = [
  {
    title: 'Delegate',
    desc: 'Whether you are a student, graduate, entrepreneur, innovator, or young professional — the Summit offers an environment to learn, grow, and connect.',
    cta: 'Register to Attend',
    path: '/youth-summit/register',
    icon: '🎓',
  },
  {
    title: 'Exhibitor',
    desc: 'Showcase your organization, programs, products, or services to hundreds of talented young people actively looking for employment, education, and innovation opportunities.',
    cta: 'Book a Stand',
    path: '/youth-summit/register?type=exhibitor',
    icon: '🏢',
  },
  {
    title: 'Partner',
    desc: 'Collaborate with YOCILAB to deliver impactful sessions, launch initiatives, recruit talent, support innovation, or create opportunities that empower young people.',
    cta: 'Become a Partner',
    path: '/contact',
    icon: '🤝',
  },
  {
    title: 'Sponsor',
    desc: 'Position your organization as a champion of youth development while supporting one of Cameroon\'s emerging platforms for employability, entrepreneurship, and leadership.',
    cta: 'View Sponsorship',
    path: '/contact',
    icon: '⭐',
  },
];

const YouthSummit = () => {
  const pageRef = useRef(null);
  const countdown = useCountdown('2026-10-30T08:00:00');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.08 }
    );
    const targets = pageRef.current?.querySelectorAll('.fade-up, .slide-left, .slide-right');
    targets?.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="summit-page" ref={pageRef}>
      <SEO
        title="YOCILAB Youth Summit 2026 | 30th & 31st October"
        description="Africa's emerging platform for employability, innovation and youth leadership — 30th and 31st of October 2026, Bamenda, Cameroon. 500+ delegates, 25+ partners."
      />

      {/* ─── HERO ─── */}
      <section className="summit-hero">
        <div className="summit-hero__bg">
          <img src={summitHero} alt="YOCILAB Youth Summit" className="summit-hero__img" />
          <div className="summit-hero__overlay" />
        </div>
        <div className="container">
          <div className="summit-hero__badge-row">
            <span className="summit-label">YOCILAB YOUTH SUMMIT</span>
            <span className="summit-label">2026</span>
          </div>

          <h1 className="summit-hero__title">
            Connecting Talent<br />
            to <em>Opportunity</em>
          </h1>

          <p className="summit-hero__subtitle">
            Africa's Emerging Platform for Employability, Innovation and Youth Leadership
          </p>

          <div className="summit-hero__meta-chips">
            <span className="summit-hero__chip">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              30th &amp; 31st October 2026
            </span>
            <span className="summit-hero__chip">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              Bamenda, Cameroon
            </span>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section className="summit-stats">
        <div className="mosaic-grid">
          <div className="mosaic-tile mosaic-tile--photo">
            <img src={campusTourImg} alt="Campus Tour outreach" loading="lazy" />
          </div>
          <div className="mosaic-tile mosaic-tile--stat-dark" style={{ background: 'var(--color-summit)' }}>
            <span className="mosaic-tile__value" style={{ color: 'var(--color-summit-gold)' }}>500+</span>
            <span className="mosaic-tile__label">Delegates</span>
          </div>
          <div className="mosaic-tile mosaic-tile--stat-dark" style={{ background: 'var(--color-accent)', color: 'var(--color-summit)' }}>
            <span className="mosaic-tile__value" style={{ color: 'var(--color-summit)' }}>25+</span>
            <span className="mosaic-tile__label" style={{ color: 'var(--color-summit)' }}>Partner Organizations</span>
          </div>
          <div className="mosaic-tile mosaic-tile--stat-light">
            <span className="mosaic-tile__value" style={{ color: 'var(--color-summit)' }}>20–30</span>
            <span className="mosaic-tile__label" style={{ color: 'var(--color-text)' }}>Innovation Challenge Teams</span>
          </div>
          <div className="mosaic-tile mosaic-tile--photo">
            <img src={experienceImg} alt="Summit Experience" loading="lazy" />
          </div>
          <div className="mosaic-tile mosaic-tile--stat-dark" style={{ background: 'var(--color-summit)' }}>
            <span className="mosaic-tile__value" style={{ color: 'var(--color-summit-gold)' }}>5,000+</span>
            <span className="mosaic-tile__label">Young People Reached via Campus Tour</span>
          </div>
        </div>
      </section>

      {/* ─── PILLARS ─── */}
      <section className="summit-pillars">
        <div className="container">
          <div className="summit-pillars__header fade-up">
            <span className="section-label" style={{ color: 'var(--color-summit-gold)' }}>FOUR PILLARS</span>
            <h2 className="summit-pillars__title">The YOCILAB Youth Summit Experience</h2>
            <p className="summit-pillars__desc">
              Built around four connected pillars that prepare young people, connect them with opportunities, foster innovation, and support their continued growth beyond the Summit.
            </p>
          </div>

          <div className="summit-pillars__grid">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                className={`summit-pillar-card fade-up ${p.highlight ? 'summit-pillar-card--featured' : ''}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="summit-pillar-card__img-wrapper">
                  <img src={p.img} alt={p.title} className="summit-pillar-card__img" loading="lazy" />
                  <div className="summit-pillar-card__overlay" />
                </div>
                <div className="summit-pillar-card__content">
                  <span className="summit-pillar-card__sub">{p.subtitle}</span>
                  <h3 className="summit-pillar-card__title">{p.title}</h3>
                  <p className="summit-pillar-card__desc">{p.desc}</p>
                  <Link to={p.path} className="summit-pillar-card__cta">
                    {p.cta}
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHY THE SUMMIT ─── */}
      <section className="summit-why">
        <div className="container">
          <div className="summit-why__grid">
            <div className="summit-why__visual slide-left">
              <img src={introImg} alt="YOCILAB Youth Summit" className="summit-why__img" loading="lazy" />
            </div>
            <div className="summit-why__content slide-right">
              <span className="section-label">WHY THE SUMMIT EXISTS</span>
              <h2 className="summit-why__title">Bridging talent with opportunity</h2>
              <p className="summit-why__body">
                Young people are Africa's greatest asset, yet millions remain disconnected from employment, entrepreneurship, mentorship, funding, and leadership opportunities.
              </p>
              <p className="summit-why__body">
                The YOCILAB Youth Summit exists to bridge that gap by connecting talent with the people, organizations, and opportunities that help young people succeed.
              </p>
              <p className="summit-why__body">
                Our vision is to build one of Africa's leading youth platforms where ideas become action, partnerships create impact, and every talented young person has the opportunity to thrive.
              </p>
              <div className="summit-why__ctas">
                <Link to="/youth-summit/register" className="summit-why__btn summit-why__btn--primary">
                  Register for Summit
                </Link>
                <Link to="/about/who-are-we" className="summit-why__btn summit-why__btn--text">
                  Learn more about YOCILAB →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BANNER ─── */}
      <section className="summit-banner">
        <div className="summit-banner__img-wrapper">
          <img src={summitBanner} alt="YOCILAB Youth Summit 2026" className="summit-banner__img" loading="lazy" />
          <div className="summit-banner__overlay" />
          <div className="container">
            <div className="summit-banner__content">
              <h2 className="summit-banner__title">The Inaugural Summit is Coming to Bamenda</h2>
              <p className="summit-banner__desc">Join over 500 young people from across Cameroon for two inspiring days of learning, networking, innovation, and collaboration on 30th &amp; 31st October 2026.</p>
              <Link to="/youth-summit/register" className="summit-banner__btn">
                Register Now →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── AUDIENCE ─── */}
      <section className="summit-audience">
        <div className="container">
          <div className="summit-audience__header fade-up">
            <span className="section-label">FIND YOUR PLACE</span>
            <h2 className="summit-audience__title">Who is the Summit for?</h2>
            <p className="summit-audience__desc">
              Whether you're a student, employer, partner, or investor — there is a place for you at the YOCILAB Youth Summit.
            </p>
          </div>

          <div className="summit-audience__grid">
            {audienceCards.map((a, i) => (
              <div key={a.title} className="summit-audience-card fade-up" style={{ transitionDelay: `${i * 100}ms` }}>
                <span className="summit-audience-card__icon">{a.icon}</span>
                <h3 className="summit-audience-card__title">{a.title}</h3>
                <p className="summit-audience-card__desc">{a.desc}</p>
                <Link to={a.path} className="summit-audience-card__cta">
                  {a.cta}
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SCHOLARSHIPS ─── */}
      <section className="summit-scholarships">
        <div className="container">
          <div className="summit-scholarships__content fade-up">
            <span className="section-label">SUMMIT SCHOLARSHIPS</span>
            <h2 className="summit-scholarships__title">No financial barrier should prevent a young person from accessing opportunity.</h2>
            <p className="summit-scholarships__desc">
              Through the support of our partners, a limited number of Summit Scholarships will be awarded to outstanding young people who demonstrate leadership potential, community impact, innovation, or financial need. Scholarships may cover registration, transportation, accommodation, and meals.
            </p>
            <Link to="/youth-summit/register?type=scholarship" className="summit-scholarships__btn">
              Apply for a Summit Scholarship
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YouthSummit;
